import type { ComponentType, ReactNode } from "react";
import Card from "../../UI/Card";
import Callout from "../../UI/Callout";
import Text from "../../UI/Text";
import Avatar from "../../UI/Avatar";
import AvatarStack from "../../UI/AvatarStack";
import Status from "../../UI/Status";
import Button from "../../UI/Button";
import IconButton from "../../UI/IconButton";
import InfoItem from "../../UI/InfoItem";
import Accordion, { AccordionItem } from "../../UI/Accordion";
import moment from "moment/moment";
import { Link, useNavigate, useParams } from "react-router-dom";
// @ts-expect-error pure js
import { useModal } from "../../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import useProperties from "../../../hooks/use-properties";
// @ts-expect-error pure js
import useTenants from "../../../hooks/use-tenants";
// @ts-expect-error pure js
import useLeaseTemplates from "../../../hooks/use-lease-templates";
// @ts-expect-error pure js
import { useSumBalanceTenants } from "../../../hooks/use-balances";
// @ts-expect-error pure js
import { TenantStatus } from "../../../models/tenant_old.js";
// @ts-expect-error pure js
import { ProfileStatus } from "../../../models/profile";
import {
  capitalizeFirstLetter,
  currencyFormatter,
  // @ts-expect-error pure js
} from "../../../api/Functions";
// @ts-expect-error pure js
import DisplayName from "../../UI/DisplayName";
// @ts-expect-error pure js
import TenantAddForm from "../../Tenant/Forms/TenantAddForm";
// @ts-expect-error pure js
import CandidatAddForm from "../../Tenant/Forms/CandidatAddForm";
// @ts-expect-error pure js
import AddLocataireChoices from "../../Tenant/AddLocataireChoices";
// @ts-expect-error pure js
import SelectPropertyTenantAction from "../../UI/SelectPropertyTenantAction";
import ShowTenantList from "../../Tenant/ShowTenantList";
import { getStatusInfo } from "../../Tenant/tenantStatus";
import { getRecurrence } from "../../../utils/rent";
// @ts-expect-error pure js
import DescriptionFinanceForm from "./DescriptionFinanceForm";
// @ts-expect-error pure js
import PartForm from "../../UI/PartForm";
import EquipementsLocal from "../AddFormComponents/EquipementsLocal";
import {
  PROPERTY_INITIAL_VALUES,
  propertyEquipementsLocalSchema,
} from "../../../models/property.js";
// @ts-expect-error pure js
import { toInitialValuesFromSchema } from "../../../models/utils";
import { getInitials } from "../../Profile/profileUtils";
import {
  IconArmchair,
  IconBath,
  IconBolt,
  IconBuilding,
  IconBuildingBank,
  IconBuildingCommunity,
  IconCalendar,
  IconCash,
  IconDevices,
  IconDoor,
  IconDroplet,
  IconEye,
  IconFlame,
  IconGasStation,
  IconHome,
  IconLeaf,
  IconPencil,
  IconPlant,
  IconPlus,
  IconRuler2,
  IconShield,
  IconSofa,
  IconToolsKitchen2,
  IconToiletPaper,
  IconWifi,
} from "@tabler/icons-react";
import style from "./VueEnsemble.module.css";
import LeaseConditionsCard from "../../UI/LeaseConditionsCard";
// @ts-expect-error pure js
import { TextOneOrMany } from "../../../utils";
import Title from "../../UI/Title.tsx";
import type { Tenant } from "../../../models/tenant";
import type { Property } from "../../../models/property";
import type { LeaseTemplate } from "../../../models/leaseTemplate";

const PIECE_ICONS: Record<string, ComponentType<{ size?: number | string }>> = {
  "Entrée indépendante": IconDoor,
  Salon: IconSofa,
  "Salon avec cuisine": IconSofa,
  "Salle de bain": IconBath,
  WC: IconToiletPaper,
  "Chambre 1": IconArmchair,
  "Chambre 2": IconArmchair,
  "Chambre 3": IconArmchair,
  Balcon: IconPlant,
  Terrasse: IconPlant,
  Cuisine: IconToolsKitchen2,
  "Pièce de vie unique (studio)": IconHome,
  "Espace de vente": IconBuilding,
  Réserve: IconBuilding,
  Bureau: IconBuilding,
  Entrepôt: IconBuilding,
  "Local technique": IconBuilding,
  Vestiaire: IconBuilding,
};

interface SumBalance {
  id_tenant: number;
  balance: number;
}

interface TenantCardProps {
  currentTenant: Tenant[];
  futurTenants: Tenant[];
  sumBalances: SumBalance[];
  openTenantChoicesAddModal: () => void;
  openHistoriqueModal: () => void;
}

function TenantCard({
  currentTenant,
  futurTenants,
  sumBalances,
  openTenantChoicesAddModal,
  openHistoriqueModal,
}: TenantCardProps) {
  const activeProfiles =
    currentTenant.length > 0
      ? currentTenant[0].profiles.filter(
          (profile) =>
            profile.garantor_of === null &&
            profile.status === ProfileStatus.Actif.status,
        )
      : [];
  const statusInfo =
    currentTenant.length > 0
      ? getStatusInfo(
          sumBalances.find((bal) => bal.id_tenant === currentTenant[0].id),
          currentTenant[0],
        )
      : null;
  return (
    <Card className={style.tenantCard}>
      <Title variant="deca" weight="bold">
        Locataire
      </Title>

      <div className={style.tenantSection}>
        <Text weight="medium">
          <TextOneOrMany
            one="Locataire actuel"
            many="Locataires actuels"
            number={activeProfiles.length}
          />
        </Text>
        {currentTenant.length > 0 && activeProfiles.length > 0 ? (
          <Link
            to={`/bailleur/tenants/${currentTenant[0].id}`}
            className={style.profileRow}
          >
            <AvatarStack
              items={activeProfiles}
              getInitials={getInitials}
              getKey={(profile, index) => profile.id ?? index}
              size="md"
            />
            <Text as="div" color="neutral" className={style.profileInfo}>
              <Text weight="bold">
                <DisplayName profile={activeProfiles[0]} />
                {activeProfiles.length > 1 && (
                  <TextOneOrMany
                    one=" et 1 autre"
                    many={` et ${activeProfiles.length - 1} autres`}
                    number={activeProfiles.length - 1}
                  />
                )}
              </Text>
              <div className={style.profileMeta}>
                <Text variant="deci" weight="medium" color="muted">
                  Loyer
                </Text>
                <div className={style.profileMetaRight}>
                  <Text weight="bold" as="span">
                    {currencyFormatter(
                      (currentTenant[0].loyer_hc ?? 0) +
                        (currentTenant[0].charges ?? 0) +
                        (currentTenant[0].autre_appel?.reduce(
                          (sum, appel) =>
                            sum +
                            (appel.remboursement
                              ? -Math.abs(appel.montant || 0)
                              : appel.montant || 0),
                          0,
                        ) ?? 0),
                    )}
                    <Text variant="deci" as="span" color="muted">
                      {" "}
                      {getRecurrence(currentTenant[0].payment_recurrence)}
                    </Text>
                  </Text>
                  {statusInfo && (
                    <Status color={statusInfo.status} text={statusInfo.text} />
                  )}
                </div>
              </div>
              <div className={style.profileMeta}>
                <Text variant="deci" weight="medium" color="muted">
                  Date du bail
                </Text>
                <Text variant="deci" weight="medium">
                  {moment(currentTenant[0].debut, "YYYY-MM-DD").format(
                    "D MMM YYYY",
                  )}
                  {" - "}
                  {moment(currentTenant[0].fin, "YYYY-MM-DD").format(
                    "D MMM YYYY",
                  )}
                  {currentTenant[0].preavis && " - Préavis déposé"}
                </Text>
              </div>
            </Text>
          </Link>
        ) : (
          <Card padding="xs" type="grey">
            <Text>Pas de locataire actuellement</Text>
          </Card>
        )}
      </div>

      <div className={style.tenantSection}>
        <Text weight="medium">
          <TextOneOrMany
            one="Prochain locataire"
            many="Prochains locataires"
            number={futurTenants.length}
          />
        </Text>
        {futurTenants.length > 0 ? (
          futurTenants.map((futurTenant) =>
            futurTenant.profiles
              .filter(
                (profile) =>
                  profile.garantor_of === null &&
                  profile.status === ProfileStatus.Actif.status,
              )
              .map((profile) => (
                <div key={profile.id} className={style.profileRow}>
                  <Avatar initials={getInitials(profile)} size="md" />
                  <div className={style.profileInfo}>
                    <Text weight="bold">
                      <Link to={`/bailleur/tenants/${futurTenant.id}`}>
                        <DisplayName profile={profile} />
                      </Link>
                    </Text>
                    <Text variant="deci" color="muted">
                      Arrivée le{" "}
                      {moment(futurTenant.debut, "YYYY-MM-DD").format(
                        "DD MMMM YYYY",
                      )}
                    </Text>
                  </div>
                </div>
              )),
          )
        ) : (
          <div className={style.emptyState}>
            <Text weight="bold">Aucun prochain locataire</Text>
            <Text variant="deci" color="muted">
              Invitez dès maintenant vos prochains locataires.
            </Text>
          </div>
        )}
      </div>

      <div className={style.tenantButtons}>
        <Button
          variant="secondary"
          icon={IconEye}
          onClick={openHistoriqueModal}
        >
          Consulter l'historique
        </Button>
        <Button
          variant="primary"
          icon={IconPlus}
          onClick={openTenantChoicesAddModal}
          requiresWrite
          data-tour="bien-add-tenant"
        >
          Ajouter
        </Button>
      </div>
    </Card>
  );
}

function ChipItem({
  icon: Icon,
  label,
}: {
  icon: ComponentType<{ size?: number | string }>;
  label: string;
}) {
  return (
    <div className={style.accordionGridItem}>
      <div className={style.accordionGridItemIcon}>
        <Icon />
      </div>
      <Text variant="deci" weight="medium">
        {label}
      </Text>
    </div>
  );
}

function DetailItem({
  icon: Icon,
  label,
  value,
}: {
  icon: ComponentType<{ size?: number | string }>;
  label: string;
  value: ReactNode;
}) {
  return (
    <div className={style.accordionGridItem}>
      <div className={style.accordionGridItemIcon}>
        <Icon size={24} />
      </div>
      <div>
        <Text variant="deci" weight="medium" color="muted">
          {label}
        </Text>
        <Text variant="deci" weight="bold">
          {value}
        </Text>
      </div>
    </div>
  );
}

interface DescriptionBienCardProps {
  property: Property;
  openDescriptionForm: () => void;
}

function DescriptionBienCard({
  property,
  openDescriptionForm,
}: DescriptionBienCardProps) {
  return (
    <Card className={style.descriptionCard}>
      <div className={style.cardHeader}>
        <Text variant="deca" weight="bold">
          Description du bien
        </Text>
        <IconButton
          onClick={openDescriptionForm}
          icon={IconPencil}
          label="Modifier"
          requiresWrite
          data-tour="bien-edit-description"
        />
      </div>

      {property.type !== "garage" && (
        <div className={style.descriptionGrid}>
          <InfoItem
            icon={IconRuler2}
            label={
              property.type === "commercial"
                ? "Superficie"
                : "Superficie habitable"
            }
            value={
              property.surface_total
                ? `${property.surface_total}m²`
                : "Non renseigné"
            }
          />
          <InfoItem
            icon={IconDoor}
            label="Nombre de pièces principales"
            value={
              property.nb_pieces_principales
                ? `${property.nb_pieces_principales} pièce${
                    property.nb_pieces_principales > 1 ? "s" : ""
                  }`
                : "Non renseigné"
            }
          />
          <InfoItem
            icon={IconCalendar}
            label="Date d'achat"
            value={
              property.date_achat
                ? moment(property.date_achat).format("DD MMMM YYYY")
                : "Non renseignée"
            }
          />
          <InfoItem
            icon={IconBuilding}
            label="Régime du logement"
            value={
              property.type_habitat
                ? `${capitalizeFirstLetter(property.type_habitat)} en ${
                    property.type_propriete
                  }`
                : "Non renseigné"
            }
          />
          <InfoItem
            icon={IconCalendar}
            label="Année de construction"
            value={property.year_built || "Non renseigné"}
          />
          <InfoItem
            icon={IconShield}
            label="Besoin GLI"
            value={property.gli ? "Oui" : "Non"}
          />
        </div>
      )}

      <Accordion
        className={style.accordions}
        divider="border"
        defaultValue={["composition"]}
      >
        {property.type !== "garage" && (
          <AccordionItem
            variant="plain"
            value="composition"
            title="Composition du logement"
          >
            <div className={style.accordionGrid}>
              {property.autres_pieces && property.autres_pieces.length > 0 ? (
                property.autres_pieces.map((piece, i) => (
                  <ChipItem
                    key={i}
                    icon={PIECE_ICONS[piece] ?? IconHome}
                    label={piece}
                  />
                ))
              ) : (
                <Text variant="deci" color="muted">
                  Non renseigné
                </Text>
              )}
            </div>
          </AccordionItem>
        )}

        {property.type === "habitation" && (
          <>
            <AccordionItem
              variant="plain"
              value="partie-commune"
              title="Partie de l'immeuble à usage commun"
            >
              <div className={style.accordionGrid}>
                {property.locaux_communs &&
                property.locaux_communs.length > 0 ? (
                  property.locaux_communs.map((item, i) => (
                    <ChipItem
                      key={i}
                      icon={IconBuildingCommunity}
                      label={item}
                    />
                  ))
                ) : (
                  <Text variant="deci" color="muted">
                    Non renseigné
                  </Text>
                )}
              </div>
            </AccordionItem>

            {property.is_coloc && (
              <>
                <AccordionItem
                  variant="plain"
                  value="coloc-commun"
                  title="Colocation, parties du logement à usage commun"
                >
                  <div className={style.accordionGrid}>
                    {property.logement_espaces_communs &&
                    property.logement_espaces_communs.length > 0 ? (
                      property.logement_espaces_communs.map((item, i) => (
                        <ChipItem key={i} icon={IconHome} label={item} />
                      ))
                    ) : (
                      <Text variant="deci" color="muted">
                        Non renseigné
                      </Text>
                    )}
                  </div>
                </AccordionItem>

                <AccordionItem
                  variant="plain"
                  value="coloc-privatif"
                  title="Colocation, parties du logement à usage privatif"
                >
                  <div className={style.accordionGrid}>
                    {property.logement_espaces_privatifs &&
                    property.logement_espaces_privatifs.length > 0 ? (
                      property.logement_espaces_privatifs.map((item, i) => (
                        <ChipItem key={i} icon={IconHome} label={item} />
                      ))
                    ) : (
                      <Text variant="deci" color="muted">
                        Non renseigné
                      </Text>
                    )}
                  </div>
                </AccordionItem>
              </>
            )}

            <AccordionItem
              variant="plain"
              value="caracteristique"
              title="Caractéristique du logement"
            >
              <div className={style.accordionGrid}>
                <DetailItem
                  icon={IconDroplet}
                  label="Eau chaude"
                  value={
                    property.eau_chaude === "individuel"
                      ? "Production individuelle"
                      : "Production collective"
                  }
                />
                <DetailItem
                  icon={IconFlame}
                  label="Chauffage"
                  value={
                    property.chauffage === "individuel"
                      ? "Individuel"
                      : "Collectif"
                  }
                />
                <DetailItem
                  icon={IconWifi}
                  label="Internet"
                  value={
                    property.technologies && property.technologies.length > 0
                      ? property.technologies.join(", ")
                      : "Non renseigné"
                  }
                />
                <DetailItem
                  icon={IconDevices}
                  label="Équipement du logement"
                  value={
                    property.equipements && property.equipements.length > 0
                      ? property.equipements.join(", ")
                      : "Non renseigné"
                  }
                />
              </div>
            </AccordionItem>

            <AccordionItem
              variant="plain"
              value="infos-techniques"
              title="Informations techniques et administratives"
            >
              <div className={style.accordionGrid}>
                <DetailItem
                  icon={IconDroplet}
                  label="Compteur d'eau"
                  value={property.compteur_eau ?? "Non renseigné"}
                />
                <DetailItem
                  icon={IconBolt}
                  label="Compteur d'électricité"
                  value={property.compteur_elec ?? "Non renseigné"}
                />
                <DetailItem
                  icon={IconGasStation}
                  label="Compteur de gaz"
                  value={property.compteur_gaz ?? "Non renseigné"}
                />
                <DetailItem
                  icon={IconWifi}
                  label="Compteur fibre"
                  value={property.compteur_fibre ?? "Non renseigné"}
                />
                <DetailItem
                  icon={IconLeaf}
                  label="Indice DPE"
                  value={property.indice_dpe ?? "Non renseigné"}
                />
                <DetailItem
                  icon={IconCash}
                  label="Montant des dépenses théoriques"
                  value={
                    property.depenses_energetiques_min &&
                    property.depenses_energetiques_max
                      ? `${property.depenses_energetiques_min}–${property.depenses_energetiques_max} €/an`
                      : "Non renseigné"
                  }
                />
                <DetailItem
                  icon={IconBuildingBank}
                  label="IBAN"
                  value={
                    property.iban
                      ? [property.iban, property.iban_titulaire]
                          .filter(Boolean)
                          .join(" — ")
                      : "Non renseigné"
                  }
                />
              </div>
            </AccordionItem>
          </>
        )}

        {property.description && (
          <AccordionItem
            variant="plain"
            value="description"
            title="Description du bien"
          >
            <Text variant="deci" style={{ whiteSpace: "pre-wrap" }}>
              {property.description}
            </Text>
          </AccordionItem>
        )}

        {property.description_public && (
          <AccordionItem
            variant="plain"
            value="description-publique"
            title="Description publique du bien"
          >
            <Text variant="deci" style={{ whiteSpace: "pre-wrap" }}>
              {property.description_public}
            </Text>
          </AccordionItem>
        )}
      </Accordion>
    </Card>
  );
}

interface VueEnsembleProps {
  property: Property;
  submitHandler: (values: Record<string, unknown>) => Promise<void> | void;
}

export default function VueEnsemble({
  property,
  submitHandler,
}: VueEnsembleProps) {
  const { propertyId } = useParams<{ propertyId: string }>();
  const propertyIdNum = parseInt(propertyId ?? "", 10);
  const nav = useNavigate();
  const { properties } = useProperties();
  const { tenants, isLoading: isLoadingTenants } = useTenants() as {
    tenants: Tenant[];
    isLoading: boolean;
  };
  const { sumBalances } = useSumBalanceTenants() as {
    sumBalances: SumBalance[];
  };
  const { leaseTemplates } = useLeaseTemplates() as {
    leaseTemplates: LeaseTemplate[] | undefined;
  };

  const [TenantAddModal, , openTenantAddModal] = useModal();
  const [CandidatAddModal, closeCandidatAddModal, openCandidatAddModal] =
    useModal();
  const [ChoseTenant, , openChoseTenant] = useModal();
  const [
    TenantAddChoicesModal,
    closeTenantChoicesAddModal,
    openTenantChoicesAddModal,
  ] = useModal();
  const [HistoriqueModal, , openHistoriqueModal] = useModal();
  const [FinanceFormModal, closeFinanceFormModal, openFinanceFormModal] =
    useModal();
  const [DescriptionModal, closeDescriptionModal, openDescriptionModal] =
    useModal();

  const currentTenant = tenants.filter(
    (filteredTenant) =>
      moment().isBetween(filteredTenant.debut, filteredTenant.fin) &&
      filteredTenant.id_bien === propertyIdNum &&
      filteredTenant.status === TenantStatus.Actif.status,
  );

  const futurTenants = tenants.filter(
    (filteredTenant) =>
      moment(filteredTenant.debut).isAfter() &&
      filteredTenant.id_bien === propertyIdNum &&
      filteredTenant.status === TenantStatus.Actif.status,
  );

  const leaseTemplate = leaseTemplates?.find(
    (lease) => property.id_lease_template === lease.id,
  );

  const leaseConditions = {
    ...property,
    debut: null,
    fin: null,
    last_revision_date: null,
  };

  const initialValues = toInitialValuesFromSchema(
    propertyEquipementsLocalSchema,
    property,
    PROPERTY_INITIAL_VALUES,
  );

  return (
    <>
      <TenantAddChoicesModal>
        <AddLocataireChoices
          addLocataire={() => {
            closeTenantChoicesAddModal();
            openTenantAddModal();
          }}
          addAvenant={() => {
            closeTenantChoicesAddModal();
            openChoseTenant();
          }}
          inviteCandidat={() => {
            closeTenantChoicesAddModal();
            openCandidatAddModal();
          }}
        />
      </TenantAddChoicesModal>
      <ChoseTenant>
        <SelectPropertyTenantAction
          tenants={tenants.filter((tenant) => tenant.id_bien === propertyIdNum)}
          properties={(properties as { id: number }[]).filter(
            (p) => p.id === propertyIdNum,
          )}
          filtering={"tenants"}
          onClick={(id: number) =>
            nav(
              `/bailleur/tenants/${id}?tab-tenant-show=actions&action=avenant&avenant-type=changement_locataire`,
            )
          }
          title={
            <>
              <img
                src={"/ChoseAjouterLocataire.jpg"}
                alt={"ajouter locataire"}
              />
              <p>Avenant au bail</p>
            </>
          }
          text={
            "Sélectionnez le bail / locataire pour lequel vous souhaitez faire un avenant"
          }
        />
      </ChoseTenant>
      <TenantAddForm propertyId={propertyIdNum} modal={TenantAddModal} />
      <CandidatAddForm
        modal={CandidatAddModal}
        propertyId={propertyIdNum}
        onClose={closeCandidatAddModal}
      />
      <HistoriqueModal>
        {tenants.length !== 0 &&
        tenants.filter(
          (filteredTenants) => filteredTenants.id_bien === propertyIdNum,
        ).length !== 0 ? (
          <ShowTenantList
            tenants={tenants.filter(
              (filteredTenants) => filteredTenants.id_bien === propertyIdNum,
            )}
            isLoading={isLoadingTenants}
          />
        ) : (
          <Callout variant="warning">Aucun ancien locataire trouvé</Callout>
        )}
      </HistoriqueModal>
      <FinanceFormModal size="big">
        <DescriptionFinanceForm
          property={property}
          closeModal={closeFinanceFormModal}
        />
      </FinanceFormModal>
      <DescriptionModal size="big">
        <PartForm
          partForm={<EquipementsLocal edit={!!property.id} />}
          validationSchema={propertyEquipementsLocalSchema}
          initialValues={initialValues}
          submitHandler={submitHandler}
          closeModal={closeDescriptionModal}
        />
      </DescriptionModal>

      <div className={style.tenantAndLease}>
        <TenantCard
          currentTenant={currentTenant}
          futurTenants={futurTenants}
          sumBalances={sumBalances}
          openTenantChoicesAddModal={openTenantChoicesAddModal}
          openHistoriqueModal={openHistoriqueModal}
        />

        <LeaseConditionsCard
          leaseConditions={leaseConditions}
          leaseTemplate={leaseTemplate}
          onEdit={openFinanceFormModal}
        />
      </div>

      <DescriptionBienCard
        property={property}
        openDescriptionForm={openDescriptionModal}
      />
    </>
  );
}
